import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Focus, Maximize2 } from "lucide-react";
import {
  Background,
  BackgroundVariant,
  Controls as FlowControls,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type NodeMouseHandler,
  type OnNodeDrag
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import type { HealthMapEdge, HealthMapNode as HealthMapNodeModel } from "../../../shared/types";
import "../../styles/healthMap.css";
import { HealthMapControls } from "./HealthMapControls";
import { HealthMapDetailPanel } from "./HealthMapDetailPanel";
import { HealthMapNode as HealthMapNodeComponent } from "./HealthMapNode";
import { HealthMapSpringEdge } from "./HealthMapSpringEdge";
import { HealthMapStaticEdge } from "./HealthMapStaticEdge";
import {
  clearHealthMapLayout,
  computeSphereLayout,
  computeSphereProximityEdges,
  createFlowEdges,
  createFlowNodes,
  filterHealthMap,
  getConnectedEdgeIds,
  getConnectedNodeIds,
  getMotionForConnectedEdge,
  getNodeMotion,
  idleHealthMapEdgeMotion,
  loadHealthMapLayout,
  mergeFlowNodePositions,
  projectSphereNode,
  saveHealthMapLayout,
  type HealthMapDragMotion,
  type HealthMapFlowEdge,
  type HealthMapFlowNode,
  type HealthMapViewControls
} from "./healthMapUtils";

interface HealthMapProps {
  nodes: HealthMapNodeModel[];
  edges: HealthMapEdge[];
  onOpenJob: (jobId: string) => void;
  variant?: "full" | "summary";
  autoRotate?: boolean;
  rotationSpeed?: number;
  showNodeBorders?: boolean;
}

const nodeTypes = {
  healthMapNode: HealthMapNodeComponent
};

const edgeTypes = {
  staticHealthMapEdge: HealthMapStaticEdge,
  springHealthMapEdge: HealthMapSpringEdge
};

const defaultControls: HealthMapViewControls = {
  sourceFilter: "ALL",
  statusFilter: "ALL",
  showLabels: true,
  showEdges: true,
  unhealthyOnly: false,
  layoutMode: "grid"
};

const fitViewOptions = { padding: 0.34 };
const maxSpringEdgeCount = 28;

export function HealthMap(props: HealthMapProps) {
  return (
    <ReactFlowProvider>
      <HealthMapCanvas {...props} />
    </ReactFlowProvider>
  );
}

function HealthMapCanvas({
  nodes,
  edges,
  onOpenJob,
  variant = "full",
  autoRotate = false,
  rotationSpeed = 0.005,
  showNodeBorders = true
}: HealthMapProps) {
  const isSummary = variant === "summary";
  const [controls, setControls] = useState<HealthMapViewControls>(() => ({
    ...defaultControls,
    layoutMode: isSummary ? "sphere" : defaultControls.layoutMode
  }));
  const [layout, setLayout] = useState(() => loadHealthMapLayout());
  const [selectedNodeId, setSelectedNodeId] = useState<string>();
  const [dragMotion, setDragMotion] = useState<HealthMapDragMotion>();
  const [isRotating, setIsRotating] = useState(autoRotate);
  const dragFrameRef = useRef<number | undefined>(undefined);
  const dragTrackerRef = useRef<{ nodeId: string; time: number; x: number; y: number } | undefined>(undefined);
  const canvasRef = useRef<HTMLElement | null>(null);
  const pendingDragMotionRef = useRef<HealthMapDragMotion | undefined>(undefined);
  const releaseTimerRef = useRef<number | undefined>(undefined);
  const motionEdgeIdsRef = useRef<Set<string>>(new Set());
  const rotationFrameRef = useRef<number | undefined>(undefined);
  const rotationAngleRef = useRef(0);
  const sphereBaseCoordsRef = useRef<Map<string, { x3: number; y3: number; z3: number }>>(new Map());
  const draggingNodeIdRef = useRef<string | undefined>(undefined);
  const { fitView } = useReactFlow<HealthMapFlowNode, HealthMapFlowEdge>();

  const prevLayoutModeRef = useRef(controls.layoutMode);
  useEffect(() => {
    if (prevLayoutModeRef.current !== controls.layoutMode) {
      prevLayoutModeRef.current = controls.layoutMode;
      if (controls.layoutMode !== "sphere") {
        setIsRotating(false);
      }
      window.requestAnimationFrame(() => fitView({ ...fitViewOptions, duration: 450 }));
    }
  }, [controls.layoutMode, fitView]);

  const visibleGraph = useMemo(() => filterHealthMap(nodes, edges, controls), [controls, edges, nodes]);
  const nodeById = useMemo(() => new Map(visibleGraph.nodes.map((node) => [node.id, node])), [visibleGraph.nodes]);
  const selectedNode = selectedNodeId ? nodeById.get(selectedNodeId) : undefined;
  const connectedEdges = useMemo(
    () => visibleGraph.edges.filter((edge) => edge.source === selectedNodeId || edge.target === selectedNodeId),
    [selectedNodeId, visibleGraph.edges]
  );
  const connectedMotionNodeIds = useMemo(
    () => getConnectedNodeIds(visibleGraph.edges, dragMotion?.nodeId),
    [dragMotion?.nodeId, visibleGraph.edges]
  );

  const generatedNodes = useMemo(
    () => createFlowNodes(visibleGraph.nodes, layout, controls, selectedNodeId),
    [controls, layout, selectedNodeId, visibleGraph.nodes]
  );
  const generatedEdges = useMemo(() => {
    if (controls.layoutMode === "sphere") {
      const sphereLayout = computeSphereLayout(visibleGraph.nodes);
      return computeSphereProximityEdges(visibleGraph.nodes, sphereLayout);
    }
    return createFlowEdges(visibleGraph.edges, nodeById, controls);
  }, [controls, nodeById, visibleGraph.edges, visibleGraph.nodes]);

  const [flowNodes, setFlowNodes, onNodesChange] = useNodesState<HealthMapFlowNode>(generatedNodes);
  const [flowEdges, setFlowEdges, onEdgesChange] = useEdgesState<HealthMapFlowEdge>(generatedEdges);

  // Sync sphere base 3D coords whenever nodes or sphere mode changes
  useEffect(() => {
    if (controls.layoutMode !== "sphere") return;
    const sl = computeSphereLayout(visibleGraph.nodes);
    const coords = new Map<string, { x3: number; y3: number; z3: number }>();
    sl.forEach((v, id) => coords.set(id, { x3: v.x3, y3: v.y3, z3: v.z3 }));
    sphereBaseCoordsRef.current = coords;
    rotationAngleRef.current = 0;
  }, [controls.layoutMode, visibleGraph.nodes]);

  // Rotation RAF loop — Y-axis spin
  useEffect(() => {
    if (!isRotating || controls.layoutMode !== "sphere") {
      if (rotationFrameRef.current !== undefined) {
        window.cancelAnimationFrame(rotationFrameRef.current);
        rotationFrameRef.current = undefined;
      }
      return;
    }

    const animate = () => {
      rotationAngleRef.current += rotationSpeed;
      const cosA = Math.cos(rotationAngleRef.current);
      const sinA = Math.sin(rotationAngleRef.current);
      const draggingId = draggingNodeIdRef.current;

      setFlowNodes((prev) =>
        prev.map((node) => {
          if (node.id === draggingId) return node;
          const base = sphereBaseCoordsRef.current.get(node.id);
          if (!base) return node;
          const rx3 = base.x3 * cosA + base.z3 * sinA;
          const rz3 = -base.x3 * sinA + base.z3 * cosA;
          const { x, y, baseScale, opacity } = projectSphereNode(rx3, base.y3, rz3);
          if (node.position.x === x && node.position.y === y) return node;
          return {
            ...node,
            position: { x, y },
            data: { ...node.data, baseScale, opacity }
          };
        })
      );

      rotationFrameRef.current = window.requestAnimationFrame(animate);
    };

    rotationFrameRef.current = window.requestAnimationFrame(animate);
    return () => {
      if (rotationFrameRef.current !== undefined) {
        window.cancelAnimationFrame(rotationFrameRef.current);
        rotationFrameRef.current = undefined;
      }
    };
  }, [isRotating, controls.layoutMode, rotationSpeed, setFlowNodes]);

  useEffect(() => {
    setFlowNodes(generatedNodes);
  }, [generatedNodes, setFlowNodes]);

  useEffect(() => {
    setFlowEdges(generatedEdges);
    motionEdgeIdsRef.current = new Set();
  }, [generatedEdges, setFlowEdges]);

  useEffect(() => {
    setFlowNodes((currentNodes) =>
      currentNodes.map((node) => {
        const motion = getNodeMotion(node.id, dragMotion, connectedMotionNodeIds);
        if (node.data.motion === motion) {
          return node;
        }

        return {
          ...node,
          data: {
            ...node.data,
            motion
          }
        };
      })
    );
  }, [connectedMotionNodeIds, dragMotion, setFlowNodes]);

  useEffect(() => {
    const nextMotionEdgeIds = getLimitedConnectedEdgeIds(visibleGraph.edges, dragMotion?.nodeId, maxSpringEdgeCount);
    const previousMotionEdgeIds = motionEdgeIdsRef.current;

    if (!dragMotion && !previousMotionEdgeIds.size) {
      return;
    }

    setFlowEdges((currentEdges) => {
      let changed = false;
      const nextEdges = currentEdges.map((edge) => {
        const shouldAnimate = nextMotionEdgeIds.has(edge.id);
        const wasAnimated = previousMotionEdgeIds.has(edge.id);
        if (!shouldAnimate && !wasAnimated) {
          return edge;
        }
        if (!edge.data) {
          return edge;
        }

        const nextType = shouldAnimate ? "springHealthMapEdge" : "staticHealthMapEdge";
        const nextMotion = shouldAnimate && dragMotion ? getMotionForConnectedEdge(dragMotion) : idleHealthMapEdgeMotion;
        const nextData = {
          ...edge.data,
          motion: nextMotion
        };

        if (edge.type === nextType && edge.data?.motion === nextMotion) {
          return edge;
        }

        changed = true;
        return {
          ...edge,
          type: nextType,
          data: nextData
        } as HealthMapFlowEdge;
      });

      return changed ? nextEdges : currentEdges;
    });
    motionEdgeIdsRef.current = nextMotionEdgeIds;
  }, [dragMotion, setFlowEdges, visibleGraph.edges]);

  useEffect(() => {
    return () => {
      if (dragFrameRef.current) {
        window.cancelAnimationFrame(dragFrameRef.current);
      }
      if (releaseTimerRef.current) {
        window.clearTimeout(releaseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!visibleGraph.nodes.length) {
      setSelectedNodeId(undefined);
      return;
    }

    if (!selectedNodeId || !nodeById.has(selectedNodeId)) {
      setSelectedNodeId(visibleGraph.nodes[0].id);
    }
  }, [nodeById, selectedNodeId, visibleGraph.nodes]);

  const fitVisibleGraph = useCallback(() => {
    window.requestAnimationFrame(() => {
      fitView({ ...fitViewOptions, duration: 350 });
    });
  }, [fitView]);

  useEffect(() => {
    if (!isSummary) {
      return;
    }

    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        window.setTimeout(fitVisibleGraph, 120);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [fitVisibleGraph, isSummary]);

  const openSummaryFullscreen = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas?.requestFullscreen) {
      return;
    }

    canvas.requestFullscreen()
      .then(() => {
        window.setTimeout(fitVisibleGraph, 120);
      })
      .catch(() => undefined);
  }, [fitVisibleGraph]);

  const persistLayout = useCallback(
    (nodesToPersist: HealthMapFlowNode[] = flowNodes) => {
      const nextLayout = mergeFlowNodePositions(layout, nodesToPersist);
      setLayout(nextLayout);
      saveHealthMapLayout(nextLayout);
    },
    [flowNodes, layout]
  );

  const commitDragMotion = useCallback((nextMotion: HealthMapDragMotion | undefined) => {
    pendingDragMotionRef.current = nextMotion;
    if (dragFrameRef.current) {
      return;
    }

    dragFrameRef.current = window.requestAnimationFrame(() => {
      dragFrameRef.current = undefined;
      setDragMotion(pendingDragMotionRef.current);
    });
  }, []);

  const handleNodeDragStart = useCallback<OnNodeDrag<HealthMapFlowNode>>(
    (_event, node) => {
      draggingNodeIdRef.current = node.id;
      if (releaseTimerRef.current) {
        window.clearTimeout(releaseTimerRef.current);
      }

      dragTrackerRef.current = {
        nodeId: node.id,
        time: window.performance.now(),
        x: node.position.x,
        y: node.position.y
      };
      commitDragMotion({
        active: true,
        intensity: 0,
        nodeId: node.id,
        updatedAt: window.performance.now(),
        vectorX: 0,
        vectorY: 0
      });
    },
    [commitDragMotion]
  );

  const handleNodeDrag = useCallback<OnNodeDrag<HealthMapFlowNode>>(
    (_event, node) => {
      const now = window.performance.now();
      const previous = dragTrackerRef.current;
      const elapsedFrames = previous ? Math.max(1, (now - previous.time) / 16.67) : 1;
      const deltaX = previous && previous.nodeId === node.id ? (node.position.x - previous.x) / elapsedFrames : 0;
      const deltaY = previous && previous.nodeId === node.id ? (node.position.y - previous.y) / elapsedFrames : 0;
      const vectorX = clampDragVector(deltaX);
      const vectorY = clampDragVector(deltaY);
      const intensity = Math.min(1, Math.hypot(vectorX, vectorY) / 28);

      dragTrackerRef.current = {
        nodeId: node.id,
        time: now,
        x: node.position.x,
        y: node.position.y
      };
      commitDragMotion({
        active: true,
        intensity,
        nodeId: node.id,
        updatedAt: now,
        vectorX,
        vectorY
      });
    },
    [commitDragMotion]
  );

  const handleNodeDragStop = useCallback<OnNodeDrag<HealthMapFlowNode>>(
    (_event, node, draggedNodes) => {
      draggingNodeIdRef.current = undefined;
      if (controls.layoutMode !== "sphere") {
        persistLayout(draggedNodes.length ? draggedNodes : flowNodes);
      }
      const releaseMotion = pendingDragMotionRef.current;
      dragTrackerRef.current = undefined;
      commitDragMotion({
        active: false,
        intensity: releaseMotion?.intensity || 0.55,
        nodeId: node.id,
        updatedAt: window.performance.now(),
        vectorX: releaseMotion?.vectorX || 0,
        vectorY: releaseMotion?.vectorY || 0
      });
      releaseTimerRef.current = window.setTimeout(() => {
        commitDragMotion(undefined);
      }, 680);
    },
    [commitDragMotion, flowNodes, persistLayout]
  );

  const handleNodeClick = useCallback<NodeMouseHandler<HealthMapFlowNode>>((_event, node) => {
    setSelectedNodeId(node.id);
  }, []);

  const handleNodeDoubleClick = useCallback<NodeMouseHandler<HealthMapFlowNode>>(
    (_event, node) => {
      const relatedJobId = node.data.healthNode.relatedJobId;
      if (relatedJobId) {
        onOpenJob(relatedJobId);
      }
    },
    [onOpenJob]
  );

  const resetLayout = useCallback(() => {
    clearHealthMapLayout();
    setLayout(undefined);
    window.requestAnimationFrame(() => fitVisibleGraph());
  }, [fitVisibleGraph]);

  return (
    <section
      className={[
        "health-map-shell",
        isSummary ? "health-map-shell--summary" : "",
        controls.layoutMode === "sphere" ? "health-map-shell--sphere" : "",
        showNodeBorders ? "" : "health-map-shell--no-node-borders"
      ].filter(Boolean).join(" ")}
    >
      {!isSummary && (
        <HealthMapControls
          controls={controls}
          edgeCount={visibleGraph.edges.length}
          isRotating={isRotating}
          nodeCount={visibleGraph.nodes.length}
          onChange={setControls}
          onFitView={fitVisibleGraph}
          onResetLayout={resetLayout}
          onSaveLayout={() => persistLayout()}
          onToggleRotation={() => setIsRotating((r) => !r)}
        />
      )}

      <div className="health-map-interactive-layout">
        <section ref={canvasRef} className="health-map-canvas" aria-label="Interactive operational health graph">
          {isSummary && visibleGraph.nodes.length > 0 && (
            <div className="health-map-summary-actions">
              <button
                className="health-map-summary-action-button"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  openSummaryFullscreen();
                }}
                aria-label="Open health map fullscreen"
                title="Fullscreen"
              >
                <Maximize2 size={13} aria-hidden="true" />
              </button>
              <button
                className="health-map-summary-action-button"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  fitVisibleGraph();
                }}
                aria-label="Fit health map view"
                title="Fit View"
              >
                <Focus size={13} aria-hidden="true" />
              </button>
            </div>
          )}
          {visibleGraph.nodes.length ? (
            <ReactFlow<HealthMapFlowNode, HealthMapFlowEdge>
              nodes={flowNodes}
              edges={flowEdges}
              edgeTypes={edgeTypes}
              nodeTypes={nodeTypes}
              minZoom={0.25}
              maxZoom={1.8}
              nodesDraggable={!isSummary}
              panOnDrag={!isSummary}
              panOnScroll={!isSummary}
              zoomOnScroll={!isSummary}
              zoomOnPinch={!isSummary}
              fitView
              fitViewOptions={fitViewOptions}
              elevateEdgesOnSelect
              elevateNodesOnSelect
              onlyRenderVisibleElements
              onEdgesChange={onEdgesChange}
              onNodeClick={handleNodeClick}
              onNodeDoubleClick={handleNodeDoubleClick}
              onNodeDrag={handleNodeDrag}
              onNodeDragStart={handleNodeDragStart}
              onNodeDragStop={handleNodeDragStop}
              onNodesChange={onNodesChange}
              onPaneClick={() => setSelectedNodeId(undefined)}
            >
              <Background color="rgba(126, 148, 141, 0.26)" gap={24} size={1.2} variant={BackgroundVariant.Dots} />
              {!isSummary && <FlowControls position="bottom-right" showInteractive={false} />}
            </ReactFlow>
          ) : (
            <div className="health-map-empty-state">
              <strong>No matching nodes</strong>
              <span>The current source and status filters hide every node.</span>
            </div>
          )}
        </section>

        {!isSummary && (
          <HealthMapDetailPanel
            connectedEdges={connectedEdges}
            node={selectedNode}
            onClose={() => setSelectedNodeId(undefined)}
            onOpenJob={onOpenJob}
          />
        )}
      </div>
    </section>
  );
}

function clampDragVector(value: number): number {
  return Math.max(-36, Math.min(36, value));
}

function getLimitedConnectedEdgeIds(edges: HealthMapEdge[], nodeId: string | undefined, limit: number): Set<string> {
  const connectedEdgeIds = getConnectedEdgeIds(edges, nodeId);
  if (connectedEdgeIds.size <= limit) {
    return connectedEdgeIds;
  }

  const limitedEdgeIds = new Set<string>();
  edges.some((edge) => {
    if (connectedEdgeIds.has(edge.id)) {
      limitedEdgeIds.add(edge.id);
    }

    return limitedEdgeIds.size >= limit;
  });

  return limitedEdgeIds;
}
